import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import SendbirdChat, { MetaData, SendbirdChatWith } from '@sendbird/chat';
import { GroupChannelModule } from '@sendbird/chat/groupChannel';
import * as Sentry from '@sentry/react';

import { gengql } from '@/__generated__';
import config from '@/config';
import { GraphQLClient } from '@/core/api';
import { Viewer } from '@/core/viewer';

interface AccessTokenData {
  accessToken: string;
  lastSeenAt: Date | null;
}

export function isAnonymousUser(metadata: MetaData, userId: string) {
  return metadata[`anonymous_${userId}`] === 'true';
}

export function channelUrlToId(channelUrl: string) {
  return channelUrl.replace('sendbird_group_channel_', '');
}

export function channelIdToUrl(channelId: string) {
  return `sendbird_group_channel_${channelId}`;
}

async function createSessionWithToken(
  userId: string,
  accessToken: string
): Promise<SendbirdChatWith<GroupChannelModule[]> | null> {
  if (!config.sendbirdAppId) {
    console.warn('Sendbird AppId is not set, messaging is disabled');
    return null;
  }

  const sb = SendbirdChat.init({
    appId: config.sendbirdAppId,
    modules: [new GroupChannelModule()],
  });

  await sb.connect(userId, accessToken);
  return sb;
}

const CREATE_USER_ACCESS_TOKEN = gengql(/* GraphQL */ `
  mutation createUserAccessToken {
    generateMessagingAccessToken {
      access_token
      last_seen_at
    }
  }
`);

function parseLastSeenAt(lastSeenAt: string | null | undefined) {
  if (typeof lastSeenAt !== 'string') return null;
  return new Date(parseInt(lastSeenAt));
}

export default class SendbirdHelper {
  private graphql: ApolloClient<NormalizedCacheObject>;
  private getViewer: () => Viewer;
  private lastUserId: string;
  private accessTokenPromise: Promise<AccessTokenData | null> | null;
  private sessionPromise: ReturnType<typeof createSessionWithToken> | null;

  // ENG-5251: getViewer is a lazy accessor (typically `() => store.getState().viewer`)
  // so the helper reads the current viewer at call time instead of capturing
  // a bootstrap-time snapshot. Mid-session signup / login / logout flips
  // viewer.id without re-creating this helper, and the previous capture-by-
  // reference shape meant getSession() returned null with the pre-auth id
  // forever (manifesting as "Messaging temporarily unavailable" until a
  // hard refresh).
  constructor(getViewer: () => Viewer, graphql: GraphQLClient) {
    this.getViewer = getViewer;
    this.lastUserId = '';
    this.graphql = graphql.client;
    this.accessTokenPromise = null;
    this.sessionPromise = null;
  }

  private getAccessTokenData() {
    let promise = this.accessTokenPromise;
    if (!promise) {
      // If the mutation rejects, drop the cached promise so the next call
      // can retry — otherwise a transient blip permanently breaks
      // messaging for this session until viewer.id flips.
      promise = (async () => {
        try {
          const { data } = await this.graphql.mutate({ mutation: CREATE_USER_ACCESS_TOKEN });
          const accessToken = data?.generateMessagingAccessToken?.access_token;
          if (!accessToken) return null;
          return {
            accessToken,
            lastSeenAt: parseLastSeenAt(data?.generateMessagingAccessToken?.last_seen_at),
          };
        } catch (err) {
          this.accessTokenPromise = null;
          throw err;
        }
      })();
    }
    this.accessTokenPromise = promise;
    return promise;
  }

  // Invalidate both cached promises whenever the viewer identity changes in
  // any direction (empty→set on signup/login, set→different on user-switch,
  // set→empty on logout). Returns the current id so callers can short-
  // circuit when it's empty without re-reading.
  private syncIdentity(): string {
    const currentId = this.getViewer().id ?? '';
    if (currentId !== this.lastUserId) {
      this.accessTokenPromise = null;
      this.sessionPromise = null;
      this.lastUserId = currentId;
    }
    return currentId;
  }

  getSession() {
    const userId = this.syncIdentity();
    if (!userId) {
      Sentry.addBreadcrumb({
        category: 'sendbird',
        level: 'info',
        message: 'getSession called with empty viewer.id; returning null',
      });
      return null;
    }
    let promise = this.sessionPromise;
    if (!promise) {
      promise = (async () => {
        const tokenData = await this.getAccessTokenData();
        if (!tokenData) return null;
        return await createSessionWithToken(userId, tokenData.accessToken);
      })();
    }
    this.sessionPromise = promise;
    return promise;
  }

  async isActiveUser(): Promise<boolean> {
    this.syncIdentity();
    // Consider user active if has connected to Sendbird within 7 days
    const tokenData = await this.getAccessTokenData();
    if (!tokenData || !tokenData.lastSeenAt) return false;
    const threshold = 1000 * 60 * 60 * 24 * 7;
    return Date.now() - tokenData.lastSeenAt.getTime() < threshold;
  }
}
